"use client";

import { motion } from "framer-motion";
import { Users, Target, Rocket } from "lucide-react";

export function About() {
  const features = [
    {
      icon: <Users className="w-5 h-5 text-[#D4A574]" />,
      title: "Quem Somos",
      description:
        "A Aura Digital nasceu da paixão por resultados reais. Combinamos estratégia, criatividade e dados para elevar marcas ao próximo nível no ambiente digital.",
    },
    {
      icon: <Target className="w-5 h-5 text-[#B8845A]" />,
      title: "Skin in the Game",
      description:
        "Não somos apenas teóricos. Só oferecemos o que já testamos e validamos com nosso próprio dinheiro. Sabemos o que funciona na prática.",
    },
    {
      icon: <Rocket className="w-5 h-5 text-[#E0B88A]" />,
      title: "Nossa Metodologia",
      description:
        "Posicionamento estratégico, presença digital consistente e resultados mensuráveis. Três pilares que sustentam o crescimento de toda marca que atendemos.",
    },
  ];

  return (
    <section id="about" className="py-24 relative">
      {/* Subtle section divider line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#C9956B]/40 to-transparent" />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start max-w-6xl mx-auto">
          {/* Left — Story */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-[12px] font-medium uppercase tracking-widest text-[#D4A574] mb-4 block">
              Nossa Origem
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 leading-tight">
              Nascemos da prática.{" "}
              <span className="text-zinc-500">Vivemos de resultados.</span>
            </h2>
            <div className="space-y-4 text-[15px] text-zinc-400 leading-relaxed">
              <p>
                A Aura Digital surgiu da convicção de que o mercado merece
                agências que realmente se importam com o ROI. Após anos
                escalando nossos próprios projetos, decidimos compartilhar
                nossa expertise.
              </p>
              <p>
                Com foco em posicionamento, presença digital e resultados,
                aplicamos tudo o que sabemos para elevar sua marca.
              </p>
            </div>

            {/* Brand detail */}
            <div className="mt-10 flex items-center gap-4">
              <div className="flex -space-x-2">
                {["A", "U", "R"].map((l, i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9956B]/80 to-[#A0734D]/80 flex items-center justify-center text-xs font-bold text-white ring-2 ring-[#0a0a0a]"
                  >
                    {l}
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[13px] font-medium text-zinc-300">
                  Aura Digital
                </p>
                {/* Half-filled bottom bar */}
                <div className="w-28 h-0.5 bg-zinc-800 rounded-full mt-1.5 overflow-hidden">
                  <div className="w-1/2 h-full bg-[#C9956B] rounded-full" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Feature cards */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex flex-col gap-4"
          >
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="flex gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-[#C9956B]/25 hover:bg-white/[0.04] transition-all duration-300 group"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-white/[0.05] flex items-center justify-center group-hover:bg-[#C9956B]/10 transition-colors">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1.5">
                    {feature.title}
                  </h3>
                  <p className="text-[13px] text-zinc-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
